import { Ros } from "roslib";
import { Action } from "..";
import {
  ActionCaller,
  ActionCallbacks,
  ActionHandle,
  ActionNotSupportedError,
} from "../base";

export type RosLibJsActionOptions = { ros: Ros };

/**
 * Adapter over the existing roslib-backed `Action` class, exposed behind the
 * source-agnostic ActionCaller so the roslib path keeps working unchanged.
 *
 * `monitorGoal` is unsupported: roslib/rosbridge actionlib cannot join a goal it did
 * not originate. Use the Foxglove source for monitor-by-goal-id.
 */
export class RosLibJsActionCaller extends ActionCaller {
  private ros: Ros;

  constructor(options: RosLibJsActionOptions) {
    super();
    this.ros = options.ros;
  }

  async sendGoal<TGoal = unknown, TFeedback = unknown, TResult = unknown>(
    actionName: string,
    goal: TGoal,
    callbacks: ActionCallbacks<TFeedback, TResult> = {}
  ): Promise<ActionHandle> {
    const action = new Action<TGoal, TFeedback, TResult>(this.ros, actionName);
    let goalId = "";

    await action.listen({
      onStatus: (statuses) => {
        if (!goalId) return;
        const mine = statuses.find((s) => s.goal_info.goal_id.id === goalId);
        if (mine) callbacks.onStatus?.(mine.status);
      },
      onFeedback: (fb) => callbacks.onFeedback?.(fb.feedback),
      onResult: (r) => callbacks.onResult?.(r.result, r.status),
      onFailed: (e) => callbacks.onFailed?.(e),
    });

    goalId = await action.sendGoal(goal);

    return {
      goalId,
      cancel: () => action.cancelGoal(goalId),
      stop: () => action.stopListen(),
    };
  }

  async monitorGoal(): Promise<ActionHandle> {
    throw new ActionNotSupportedError(
      "roslib action source cannot join a goal by id; use the Foxglove source"
    );
  }
}
